import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { AuthErrorMessage } from '../components/auth-mfa/AuthErrorMessage';
import { MfaSetupCompleteNotice } from '../components/auth-mfa/MfaSetupCompleteNotice';
import { SignInCompletionFailure } from '../components/auth-mfa/SignInCompletionFailure';
import { TotpCodeForm } from '../components/auth-mfa/TotpCodeForm';
import { TotpSetupForm } from '../components/auth-mfa/TotpSetupForm';
import { useSignInFlow } from '../hooks/use-sign-in-flow';
import { storeTokenHandoff } from '../services/auth-redirect';

/**
 * Amplify Auth のトークンストアからトークンを取得してアプリにリダイレクトする
 * Amplify v6 の内部 API を使用してリフレッシュトークンを含む全トークンを取得
 */
async function redirectToAppWithTokens(): Promise<void> {
  try {
    // Amplify の内部トークンストアから全トークンを取得
    const tokens = await cognitoUserPoolsTokenProvider.tokenOrchestrator
      ?.getTokenStore()
      .loadTokens();

    if (!tokens) {
      throw new Error('トークンを取得できませんでした');
    }

    const idToken = tokens.idToken?.toString();
    const accessToken = tokens.accessToken?.toString();
    const refreshToken = tokens.refreshToken?.toString();

    if (!idToken || !accessToken) {
      throw new Error('IDトークンまたはアクセストークンが見つかりません');
    }

    // 保存されたstateを取得
    const state = sessionStorage.getItem('auth_state');

    // トークンは URL に載せず sessionStorage 経由で Callback ページに受け渡す
    // （ブラウザ履歴・アクセスログ・Referer への残留対策）
    storeTokenHandoff({
      idToken,
      accessToken,
      refreshToken: refreshToken || undefined,
      state: state ?? undefined,
    });

    // Callbackページに遷移（Callbackページでアプリにリダイレクト＆完了画面表示）
    window.location.href = '/auth/callback';
  } catch (err) {
    console.error('トークン取得エラー:', err);
    throw err;
  }
}

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [initializing, setInitializing] = useState(true);
  const flow = useSignInFlow({ onSignedIn: redirectToAppWithTokens });

  // 初期化時に既存セッションをクリア
  useEffect(() => {
    const clearExistingSession = async () => {
      try {
        // 既存ユーザーがいるか確認
        await getCurrentUser();
        // 既存セッションがある場合はサインアウト
        await signOut();
      } catch {
        // ユーザーがいない場合は何もしない
      } finally {
        setInitializing(false);
      }
    };
    clearExistingSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await flow.submitCredentials(email, password);
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await flow.submitNewPassword(newPassword);
  };

  // 初期化中・完了後（アプリへのリダイレクト待ち）はローディング表示
  if (initializing || flow.step.kind === 'done') {
    return (
      <AuthLayout
        title="サインイン"
        subtitle={initializing ? '準備中...' : 'サインインしています...'}
      >
        {flow.error ? (
          <SignInCompletionFailure
            error={flow.error}
            submitting={flow.submitting}
            onRetry={flow.retryCompletion}
          />
        ) : (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-muted border-l-primary-500 rounded-full animate-spin" />
          </div>
        )}
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'totpSetup') {
    return (
      <AuthLayout
        title="認証アプリの登録"
        subtitle="サインインには、パスワードに加えて認証アプリのコードが必要です"
      >
        <TotpSetupForm
          setupUri={flow.step.setupUri}
          sharedSecret={flow.step.sharedSecret}
          submitting={flow.submitting}
          error={flow.error}
          onSubmit={flow.submitTotpSetupCode}
        />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'totpCode') {
    return (
      <AuthLayout title="確認コードの入力" subtitle="認証アプリのコードを入力してください">
        <TotpCodeForm
          submitting={flow.submitting}
          error={flow.error}
          onSubmit={flow.submitTotpCode}
          onRestart={flow.restart}
        />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'setupComplete') {
    return (
      <AuthLayout title="登録完了">
        <MfaSetupCompleteNotice onContinue={flow.acknowledgeSetupComplete} />
      </AuthLayout>
    );
  }

  if (flow.step.kind === 'newPassword') {
    return (
      <AuthLayout
        title="新しいパスワードを設定"
        subtitle="初回ログインのため、新しいパスワードを設定してください"
      >
        <form onSubmit={handleNewPassword} className="space-y-6">
          <AuthErrorMessage error={flow.error} />

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-foreground mb-2">
              新しいパスワード
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="新しいパスワードを入力"
            />
          </div>

          <button
            type="submit"
            disabled={flow.submitting}
            className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {flow.submitting ? '設定中...' : 'パスワードを設定'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="サインイン" subtitle="きゅーもAI エージェント v2 にサインイン">
      <form onSubmit={handleSubmit} className="space-y-6">
        <AuthErrorMessage error={flow.error} />

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            メールアドレス
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            パスワード
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを入力"
          />
        </div>

        <button
          type="submit"
          disabled={flow.submitting}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {flow.submitting ? 'サインイン中...' : 'サインイン'}
        </button>
      </form>

      <div className="mt-6 space-y-3">
        <Link
          to="/auth/forgot-password"
          className="block text-center text-sm text-primary-400 hover:text-primary-300 transition-colors"
        >
          パスワードをお忘れですか？
        </Link>
        <Link
          to="/auth/signup"
          className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          アカウントをお持ちでない方は
          <span className="text-primary-400">サインアップ</span>
        </Link>
      </div>
    </AuthLayout>
  );
}
